/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useRef, useState } from "react";

import { isElectron, isMobile } from "react-device-detect";
import styled from "styled-components";

import { Messages } from "../Generation/Messages";
import { useCurrentUser } from "src/app/useCurrentUser";
import { Plate } from "src/classes/plate/Plate";
import { CurrentUserAvatar } from "src/components/AppTopbar/CurrentUserAvatar";
import { NavButtons } from "src/components/AppTopbar/NavButtons";
import { StyledAppTobar, StyledTopbarLeft, StyledTopbarRight } from "src/components/AppTopbar/styles";
import { UserDropdown, ViewsDropdown } from "src/components/AppTopbar/UserDropdown";
import { EditorAvatars } from "src/components/Plate/EditorAvatars";
import { SettingsButton } from "src/components/Plate/Settings/SettingsButton";
import { ShareButton } from "src/components/Plate/ShareMenu/ShareButton";
import { SyncStatusIcon } from "src/components/Plate/Status/SyncStatusIcon";
import { Circle, CircleWrapper } from "src/components/Plate/Topbar/Circle";
import { Lock as LockButton, SignIn as TopbarSignIn, Ownername } from "src/components/Plate/Topbar/lib/Buttons";
import { PlateTitle } from "src/components/Plate/Topbar/lib/PlateTitle";
import { Connect as ConnectButton } from "src/components/UI/Buttons/IconButtons/Connect";
import { Like as LikeButton } from "src/components/UI/Buttons/IconButtons/Like";

interface Props {
  plate: Plate;
}

export const PlateTopbar = ({ plate }: Props) => {
  const isLiked = plate.observe((state) => state.isLiked);
  const currentUserIsOwner = plate.snapshot.currentUserIsOwner;
  const editable = plate.observe((state) => state.editable);
  const messages = plate.generator?.observe((state) => state.messages);

  const avatarRef = useRef(null);
  const viewsRef = useRef(null);

  const currentUser = useCurrentUser();

  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [viewsDropdownOpen, setViewsDropdownOpen] = useState(false);
  const [connectMenuOpen, setConnectMenuOpen] = useState(false);

  return (
    <StyledPlateTopbar>
      <StyledTopbarLeft>
        {currentUser && isElectron && <NavButtons />}
        <CircleWrapper>
          <Circle ref={viewsRef} onClick={() => setViewsDropdownOpen(!viewsDropdownOpen)} />
          <ViewsDropdown
            rendered={viewsDropdownOpen}
            closeDropdown={() => setViewsDropdownOpen(false)}
            viewsRef={viewsRef}
          />
        </CircleWrapper>
        {currentUserIsOwner && <LockButton plate={plate} />}
        {!currentUserIsOwner && <Ownername ownername={plate.snapshot.owner.username} />}
        <PlateTitle plate={plate} />
        {currentUser && <LikeButton isLiked={isLiked} like={plate.like} />}
        {currentUser && (
          <ConnectButton plate={plate} block={null} menuOpen={connectMenuOpen} setMenuOpen={setConnectMenuOpen} />
        )}
        {/* {plate.generator && <button onClick={() => plate.generator!.generate()}>Generate</button>} */}
        {!!messages?.length && (
          <button
            onClick={() => plate.showModal({ title: "Messages", body: <Messages messages={messages} /> })}
            style={{ marginLeft: "10px" }}
          >
            Messages
          </button>
        )}
      </StyledTopbarLeft>
      <StyledTopbarRight>
        <EditorAvatars plate={plate} />
        <ShareButton plate={plate} />
        {editable && !isMobile && (
          <>
            <SettingsButton plate={plate} />
            <SyncStatusIcon plate={plate} />
          </>
        )}
        {!currentUser ? (
          <TopbarSignIn />
        ) : (
          <CurrentUserAvatar
            ref={avatarRef}
            handleClick={() => setUserDropdownOpen(!userDropdownOpen)}
            styles={{ marginLeft: "15px" }}
          />
        )}
      </StyledTopbarRight>
      {currentUser && (
        <UserDropdown
          rendered={userDropdownOpen}
          closeDropdown={() => setUserDropdownOpen(false)}
          avatarRef={avatarRef}
        />
      )}
    </StyledPlateTopbar>
  );
};

const StyledPlateTopbar = styled(StyledAppTobar)`
  font-size: 14px;
  color: ${({ theme }) => theme.text.default};
`;
